import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, FormControl,
  MenuItem, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { toast } from "react-toastify";
import {
  PURPLE,
  PURPLE_DARK,
  PURPLE_SOFT,
  HOLIDAY_TYPES,
  HOLIDAY_NAME_PRESETS,
  detectHolidayType,
  autoDetectUKHolidays,
  courseNameOf,
  FilterChip,
} from "./ResourceAllocationShared";
import HolidayCalendarView from "./components/HolidayCalendarView";
import AiHolidayModifierDialog from "./components/AiHolidayModifierDialog";
import api from "../../api";

function useGlobalHolidayCalendar() {
  const [loading, setLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [calendarWeeks, setCalendarWeeks] = useState([]);
  const [holidayDrafts, setHolidayDrafts] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const coursesRes = await api.get("admin/ca_records?active_only=1");
      const courses = coursesRes.data?.data || [];
      setAcademicYears(courses);

      const plansArrays = await Promise.all(
        courses.map(async (c) => {
          try {
            const r = await api.get(`admin/acdemic-courses/${c.id}/course-plans-with-weeks`);
            let list = r?.data?.plans ?? r?.data?.data?.plans ?? [];
            if (!list.length) {
              const courseId = c.course_id || c.course?.id;
              if (courseId) {
                const r2 = await api.get(`admin/courses/${courseId}/course-plans-with-weeks`);
                list = r2?.data?.plans ?? r2?.data?.data?.plans ?? [];
              }
            }
            return list.map((p) => ({ ...p, _yearName: courseNameOf(c), _yearId: c.id }));
          } catch {
            return [];
          }
        })
      );
      const flat = plansArrays.flat();
      setAllPlans(flat);

      // Deduplicate by start_date — is_holiday=true wins if any group has it marked
      const weekMap = new Map();
      flat.forEach((plan) => {
        (plan.plan_weeks || plan.planWeeks || plan.weeks || []).forEach((w) => {
          const key = w.start_date;
          const ex = weekMap.get(key);
          if (!ex || w.is_holiday) {
            weekMap.set(key, { ...w, _yearName: plan._yearName, _yearId: plan._yearId });
          }
        });
      });

      const sorted = Array.from(weekMap.values()).sort((a, b) => a.start_date.localeCompare(b.start_date));
      setCalendarWeeks(sorted);
      const drafts = {};
      sorted.forEach((w) => { drafts[w.id] = { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" }; });
      setHolidayDrafts(drafts);
    } catch {
      toast.error("Failed to load holiday calendar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { loading, academicYears, allPlans, calendarWeeks, holidayDrafts, setHolidayDrafts, reload };
}

export default function HolidayManagement() {
  const { loading, academicYears, allPlans, calendarWeeks, holidayDrafts, setHolidayDrafts, reload } =
    useGlobalHolidayCalendar();

  const [yearFilter, setYearFilter] = useState(null); // null = all years
  const [selectedWeekIds, setSelectedWeekIds] = useState(new Set());
  const [bulkType, setBulkType] = useState("");
  const [bulkName, setBulkName] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [globalApplying, setGlobalApplying] = useState(false);
  const [globalResult, setGlobalResult] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const filteredWeeks = useMemo(() =>
    yearFilter == null
      ? calendarWeeks
      : calendarWeeks.filter((w) => w._yearId === yearFilter),
    [calendarWeeks, yearFilter]
  );

  const isDirty = useMemo(() =>
    calendarWeeks.some((w) => {
      const d = holidayDrafts[w.id];
      if (!d) return false;
      return d.is_holiday !== w.is_holiday || d.holiday_name !== (w.holiday_name || "");
    }),
    [calendarWeeks, holidayDrafts]
  );

  const activeHolidays = useMemo(
    () => calendarWeeks.filter((w) => (holidayDrafts[w.id] ?? { is_holiday: w.is_holiday }).is_holiday),
    [calendarWeeks, holidayDrafts]
  );

  const handleAutoDetect = () => {
    const suggestions = autoDetectUKHolidays(calendarWeeks);
    if (!Object.keys(suggestions).length) {
      toast.info("No UK school holiday weeks detected in the loaded date ranges.");
      return;
    }
    setHolidayDrafts((prev) => {
      const next = { ...prev };
      Object.entries(suggestions).forEach(([id, val]) => { next[id] = { ...(next[id] ?? {}), ...val }; });
      return next;
    });
    toast.success(`${Object.keys(suggestions).length} holiday week(s) auto-detected. Review and save.`);
  };

  const handleToggleWeekHoliday = (weekId, checked) => {
    setHolidayDrafts((prev) => ({
      ...prev,
      [weekId]: checked
        ? { ...(prev[weekId] ?? {}), is_holiday: true }
        : { is_holiday: false, holiday_name: "" },
    }));
  };

  const handleNameChange = (weekId, name) => {
    setHolidayDrafts((prev) => ({ ...prev, [weekId]: { ...(prev[weekId] ?? {}), holiday_name: name } }));
  };

  const handlePresetApply = (weekId, preset) => {
    setHolidayDrafts((prev) => ({ ...prev, [weekId]: { ...(prev[weekId] ?? {}), is_holiday: true, holiday_name: preset.name } }));
  };

  const handleReset = () => {
    const drafts = {};
    calendarWeeks.forEach((w) => { drafts[w.id] = { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" }; });
    setHolidayDrafts(drafts);
    setSelectedWeekIds(new Set());
  };

  const toggleSelectWeek = (weekId) => {
    setSelectedWeekIds((prev) => {
      const next = new Set(prev);
      next.has(weekId) ? next.delete(weekId) : next.add(weekId);
      return next;
    });
  };

  const applyBulkToSelected = () => {
    if (!selectedWeekIds.size) return;
    const name = bulkName || (HOLIDAY_NAME_PRESETS.find((p) => p.type === bulkType)?.name ?? "");
    setHolidayDrafts((prev) => {
      const next = { ...prev };
      selectedWeekIds.forEach((id) => { next[id] = { is_holiday: true, holiday_name: name }; });
      return next;
    });
    setSelectedWeekIds(new Set());
    setBulkName("");
    setBulkType("");
  };

  const handleTopPresetClick = (preset) => {
    if (selectedWeekIds.size > 0) {
      setBulkName(preset.name);
      setBulkType(preset.type);
    } else {
      toast.info("Check some weeks first to bulk-apply a preset.");
    }
  };

  const handleAiApply = (changes) => {
    setHolidayDrafts((prev) => ({ ...prev, ...changes }));
    toast.success(`${Object.keys(changes).length} AI suggestion(s) applied. Review and save.`);
  };

  const handleSave = async () => {
    const holidayRanges = calendarWeeks
      .filter((w) => holidayDrafts[w.id]?.is_holiday)
      .map((w) => ({
        start_date: w.start_date,
        end_date: w.end_date,
        holiday_name: holidayDrafts[w.id]?.holiday_name || null,
      }));

    setSaving(true);
    try {
      const r = await api.post("admin/holidays/sync-by-dates", { holidays: holidayRanges });
      const { plans_updated = 0, weeks_marked = 0 } = r.data ?? {};
      toast.success(`Saved · Synced ${weeks_marked} holiday week(s) across ${plans_updated} year(s) & group(s)`);
      reload();
    } catch {
      toast.error("Failed to save holidays");
    } finally {
      setSaving(false);
    }
  };

  const handleGlobalApply = async () => {
    setConfirmVisible(false);
    setGlobalApplying(true);
    setGlobalResult(null);
    try {
      const r = await api.post("admin/holidays/apply-uk-holidays-globally");
      setGlobalResult(r.data);
      toast.success(`UK holidays applied — ${r.data.weeks_marked} holiday week(s) across ${r.data.plans_updated} group(s).`);
      reload();
    } catch {
      toast.error("Failed to apply UK holidays globally");
    } finally {
      setGlobalApplying(false);
    }
  };

  const firstPlanId = allPlans[0]?.id ?? null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Dashboard / <Box component="span" sx={{ color: PURPLE, fontWeight: 600 }}>Holiday Management</Box>
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5} mb={0.25}>Holiday Management</Typography>
      <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }} mb={2}>
        Holiday calendar across all years &amp; groups. Toggle weeks, assign holiday names, then save to sync globally.
      </Typography>

      {/* Global Apply Banner */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #c4b5fd", bgcolor: PURPLE_SOFT }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <PublicIcon sx={{ color: PURPLE, fontSize: 28, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: PURPLE }}>
              Apply Standard UK School Holidays to All Courses &amp; Groups
            </Typography>
            <Typography variant="caption" sx={{ color: "#374151" }}>
              Automatically marks Christmas, Easter, Half-terms and Summer weeks across every year and group based on calendar dates. Holiday-week deadlines are set 2 weeks before the next teaching class.
            </Typography>
            {globalResult && (
              <Stack direction="row" spacing={0.75} mt={0.5} flexWrap="wrap">
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label={`${globalResult.weeks_marked} holiday week(s) marked`}
                  sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, height: 22 }} />
                <Chip size="small"
                  label={`${globalResult.plans_updated} group(s) updated`}
                  sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, height: 22 }} />
              </Stack>
            )}
          </Box>
          {!confirmVisible ? (
            <Button variant="contained" size="small"
              startIcon={globalApplying ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
              onClick={() => setConfirmVisible(true)} disabled={globalApplying}
              sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK }, flexShrink: 0 }}>
              {globalApplying ? "Applying…" : "Apply to All Courses & Groups"}
            </Button>
          ) : (
            <Stack direction="row" spacing={0.75} flexShrink={0}>
              <Typography variant="caption" fontWeight={700} sx={{ alignSelf: "center", color: "#374151", whiteSpace: "nowrap" }}>
                Overwrite all holiday weeks?
              </Typography>
              <Button size="small" variant="contained" onClick={handleGlobalApply}
                sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" }, whiteSpace: "nowrap" }}>
                Yes, Apply
              </Button>
              <Button size="small" variant="outlined" onClick={() => setConfirmVisible(false)}
                sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151" }}>
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Action bar + year filter */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Button variant="outlined" size="small" startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
          onClick={handleAutoDetect} disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, borderColor: PURPLE, color: PURPLE, "&:hover": { bgcolor: PURPLE_SOFT, borderColor: PURPLE_DARK } }}>
          Auto-detect UK Holidays
        </Button>
        <Button variant="outlined" size="small" startIcon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setAiDialogOpen(true)} disabled={!firstPlanId || loading}
          sx={{ textTransform: "none", fontWeight: 600, borderColor: PURPLE, color: PURPLE, "&:hover": { bgcolor: PURPLE_SOFT, borderColor: PURPLE_DARK } }}>
          AI Holiday Modifier
        </Button>

        {/* Year filter chips */}
        {academicYears.length > 1 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ ml: { sm: "auto" } }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Year:</Typography>
            <FilterChip label="All" active={yearFilter === null} onClick={() => setYearFilter(null)} />
            {academicYears.map((y) => (
              <FilterChip
                key={y.id}
                label={courseNameOf(y)}
                active={yearFilter === y.id}
                onClick={() => setYearFilter(yearFilter === y.id ? null : y.id)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={1} mb={1.25} flexWrap="wrap" useFlexGap>
        <Chip size="small"
          label={`${calendarWeeks.length} total weeks`}
          sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600, height: 22 }} />
        <Chip size="small"
          label={`${activeHolidays.length} holiday weeks`}
          sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600, height: 22 }} />
        {academicYears.length > 0 && (
          <Chip size="small"
            label={`${academicYears.length} year(s) · ${allPlans.length} group(s)`}
            sx={{ bgcolor: PURPLE_SOFT, color: PURPLE, fontWeight: 600, height: 22 }} />
        )}
      </Stack>

      {/* Active holiday summary chips */}
      {activeHolidays.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.25}>
          {activeHolidays.map((w) => {
            const d = holidayDrafts[w.id] ?? { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" };
            const htype = HOLIDAY_TYPES[detectHolidayType(d.holiday_name)];
            return (
              <Chip key={w.id} size="small"
                label={`${htype.icon} ${w.start_date.slice(5, 10).replace("-", "/")}${d.holiday_name ? `: ${d.holiday_name}` : ""}`}
                sx={{ bgcolor: htype.bg, color: htype.color, fontWeight: 600, border: `1px solid ${htype.border}` }} />
            );
          })}
        </Stack>
      )}

      {/* Bulk selection bar */}
      {selectedWeekIds.size > 0 && (
        <Paper elevation={0} sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: `1px solid ${PURPLE}`, bgcolor: PURPLE_SOFT }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flexWrap="wrap">
            <Typography variant="caption" fontWeight={700} sx={{ color: PURPLE, whiteSpace: "nowrap" }}>
              {selectedWeekIds.size} week{selectedWeekIds.size > 1 ? "s" : ""} selected
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select value={bulkType} onChange={(e) => setBulkType(e.target.value)} displayEmpty>
                <MenuItem value="">Holiday type…</MenuItem>
                {Object.entries(HOLIDAY_TYPES).map(([key, val]) => (
                  <MenuItem key={key} value={key}>{val.icon} {val.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" placeholder="Holiday name (optional)" value={bulkName}
              onChange={(e) => setBulkName(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />
            <Button size="small" variant="contained" onClick={applyBulkToSelected}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK }, whiteSpace: "nowrap" }}>
              Mark as holiday
            </Button>
            <Button size="small" variant="text" onClick={() => setSelectedWeekIds(new Set())}
              sx={{ textTransform: "none", color: "#64748b" }}>Cancel</Button>
          </Stack>
        </Paper>
      )}

      {/* Quick-add preset chips */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" mb={1.5}>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Quick add:</Typography>
        {HOLIDAY_NAME_PRESETS.map((p) => {
          const ht = HOLIDAY_TYPES[p.type];
          return (
            <Chip key={p.name} size="small" label={`${ht.icon} ${p.name}`} clickable
              onClick={() => handleTopPresetClick(p)}
              sx={{ bgcolor: ht.bg, color: ht.color, fontWeight: 600, border: `1px solid ${ht.border}`, height: 24, "&:hover": { opacity: 0.85 } }} />
          );
        })}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Calendar */}
      {loading ? (
        <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={36} sx={{ color: PURPLE }} />
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>Loading calendar…</Typography>
        </Box>
      ) : calendarWeeks.length === 0 ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <BeachAccessIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} />
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>No plan weeks found. Create courses and groups first.</Typography>
        </Box>
      ) : (
        <HolidayCalendarView
          weeks={filteredWeeks}
          drafts={holidayDrafts}
          onToggle={handleToggleWeekHoliday}
          onPresetApply={handlePresetApply}
          onNameChange={handleNameChange}
          onSelectWeek={toggleSelectWeek}
          selectedWeekIds={selectedWeekIds}
          hideWeekNumber
        />
      )}

      {/* Save footer */}
      <Divider sx={{ mt: 2, mb: 1.5 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" size="small" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleReset} disabled={saving || !isDirty}
          sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151",
            "&:hover": { borderColor: PURPLE, color: PURPLE, bgcolor: PURPLE_SOFT } }}>
          Reset
        </Button>
        <Button variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
          onClick={handleSave} disabled={saving || !isDirty}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: PURPLE,
            "&:hover": { bgcolor: PURPLE_DARK }, "&:disabled": { opacity: 0.5 } }}>
          {saving ? "Saving…" : "Save & Sync All Years & Groups"}
        </Button>
      </Stack>

      {/* AI Holiday Modifier dialog */}
      {firstPlanId && (
        <AiHolidayModifierDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          planId={firstPlanId}
          weeks={calendarWeeks}
          drafts={holidayDrafts}
          onApply={handleAiApply}
        />
      )}
    </Box>
  );
}
